"use client";

import { useEffect } from "react";
import { useSessionStorage } from "usehooks-ts";

import ActivityTabs from "@/components/dashboard/activity-tabs";
import StickyHeader from "@/components/sticky-header";
import { useDashboardTabs } from "@/hooks/useDashboardTabs";
import { useMeasuredHeight } from "@/hooks/useMeasuredHeight";
import { useCustomerAccount, useDashboardHeader } from "@/providers";
import {
  useGetProcessingPaymentsQuery,
  useGetUnconfirmedPaymentsQuery,
} from "@generated/redux/services/generated/api";

export default function DashboardTabsHeader() {
  const { customerAccount } = useCustomerAccount();
  const { setTabsHeight } = useDashboardHeader();

  // Measure the real pinned height of this tabs band so the list header below
  // pins exactly under it — across the no-tabs <hr>, tabs, tabs+badge, mobile,
  // and banner cases. Replaces hand-estimated height constants.
  const tabsBandRef = useMeasuredHeight(setTabsHeight);

  // Tab state management
  const { activeTab, setActiveTab } = useDashboardTabs();

  // sessionStorage hook - it will persist across client navigation
  // and clear on logout/login
  const [badgeDismissed, setBadgeDismissed] = useSessionStorage(
    "pending-items-badge-dismissed",
    false
  );

  // Fetch unconfirmed payments count
  const {
    data: unconfirmedPaymentsData,
    isLoading: isUnconfirmedLoading,
    isUninitialized: isUnconfirmedUninitialized,
  } = useGetUnconfirmedPaymentsQuery(
    {
      filterCustomerAccount: customerAccount?.customerAccount?.id
        ? [customerAccount.customerAccount.id]
        : [],
    },
    {
      skip: !customerAccount?.customerAccount?.id,
    }
  );

  // Fetch processing payments count
  const {
    data: processingPaymentsData,
    isLoading: isProcessingLoading,
    isUninitialized: isProcessingUninitialized,
  } = useGetProcessingPaymentsQuery(
    {
      filterCustomerAccount: customerAccount?.customerAccount?.id
        ? [customerAccount.customerAccount.id]
        : [],
    },
    {
      skip: !customerAccount?.customerAccount?.id,
    }
  );

  const unconfirmedCount =
    (unconfirmedPaymentsData?.payments?.length || 0) +
    (unconfirmedPaymentsData?.withdrawals?.length || 0);

  const processingCount =
    (processingPaymentsData?.payments?.length || 0) +
    (processingPaymentsData?.withdrawals?.length || 0);

  // Pending until both count queries have resolved (or have cache).
  // While pending, ActivityTabs stays on the compact hr layout.
  const isCountsPending =
    (unconfirmedPaymentsData === undefined ||
      processingPaymentsData === undefined) &&
    (isUnconfirmedLoading ||
      isUnconfirmedUninitialized ||
      isProcessingLoading ||
      isProcessingUninitialized);

  // Reset to activity tab if there are no items in the current tab
  useEffect(() => {
    // Don't reset until data has loaded
    if (isUnconfirmedLoading || isProcessingLoading) return;

    if (unconfirmedCount === 0 && activeTab === "pending") {
      setActiveTab("activity");
    }
    if (processingCount === 0 && activeTab === "processing") {
      setActiveTab("activity");
    }
  }, [
    unconfirmedCount,
    processingCount,
    activeTab,
    setActiveTab,
    isUnconfirmedLoading,
    isProcessingLoading,
  ]);

  // Handle badge click - switch to pending tab and dismiss badge
  const handleBadgeClick = () => {
    setActiveTab("pending");
    setBadgeDismissed(true);
  };

  // Show badge only when there are pending items and it hasn't been dismissed
  const showBadge = unconfirmedCount > 0 && !badgeDismissed;

  return (
    <StickyHeader
      ref={tabsBandRef}
      subHeader={2}
      noPageHeader
      className="pt-[1px]"
    >
      <ActivityTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        unconfirmedCount={unconfirmedCount}
        processingCount={processingCount}
        isCountsPending={isCountsPending}
        showBadge={showBadge}
        onBadgeClick={handleBadgeClick}
        onBadgeDismiss={() => setBadgeDismissed(true)}
      />
    </StickyHeader>
  );
}
